import type { ComponentType } from "react";

export const EmptyLazyFallback: ComponentType<any> = () => null;

/**
 * React.lazy requires `module.default` to be a component.
 *
 * Vite's `vite:preloadError` handler in `entry.client.tsx` calls
 * `preventDefault()` so a failed CSS/JS preload does not throw. Vite then
 * *resolves* the dynamic import to `undefined` instead of rejecting.
 * `.catch()` does not run in that case, and React.lazy does
 * `undefined.default`, which is:
 *   TypeError: Cannot read properties of undefined (reading 'default')
 *
 * That is the crash Google's URL Inspection tool captured on geosearch-desktop.
 */
export function resolveLazyModule(
  mod: { default?: ComponentType<any> } | null | undefined,
  fallback: ComponentType<any>,
): { default: ComponentType<any> } {
  if (mod != null && mod.default != null) {
    return { default: mod.default };
  }
  return { default: fallback };
}

export async function loadLazyModule(
  importer: () => Promise<any>,
  fallback: ComponentType<any> = EmptyLazyFallback,
): Promise<{ default: ComponentType<any> }> {
  try {
    return resolveLazyModule(await importer(), fallback);
  } catch (error) {
    console.error("[lazy] chunk failed to load, rendering fallback:", error);
    return { default: fallback };
  }
}
